<script setup>
// 시세가 오른쪽에서 왼쪽으로 끊김 없이 흐르는 띠.
//
// 상단 시세 바에서 쓴다(SiteHeader · SiteHeaderMobile, 자사몰 AppHeader).
// 칸 하나의 생김새는 헤더마다 달라 default 슬롯으로 받는다 — 여기서는 흐르는 일만 한다.
//
// 하는 일
//   1) 넘치는 부분을 가린다
//   2) 목록을 여러 벌 이어 붙인다
//   3) 한 벌 길이만큼 왼쪽으로 옮기는 동작을 무한 반복한다
//
// 몇 벌이 필요한지는 재서 정한다. 두 벌이면 될 것 같지만 아니다 —
// 한 벌이 보이는 폭보다 짧으면, 한 바퀴를 돌았을 때 뒤가 비어 버린다.
// 보이는 폭 ÷ 한 바퀴 거리 만큼 더 있어야 빈 자리가 생기지 않는다.
//
// 옮기는 거리는 복제본 첫 칸의 offsetLeft 다. 원본 한 벌 + 그 뒤 간격과 정확히 같아서
// 한 바퀴를 돌면 화면이 처음과 똑같아진다 — 여기서 되감아도 눈에 띄지 않는다.
//
// 윈도우의 "애니메이션 효과" 끄기(prefers-reduced-motion)는 보지 않는다.
// 어느 PC 에서 열어도 같은 화면이어야 한다는 결정이다 — 예전에는 그 설정이 켜진 PC 에서
// 시세 띠가 잘린 채 멈춰 있어 고장으로 보였다.
//
// ★ 이 파일은 세 저장소에 "복사"해서 쓴다.
//     homepage / goldnaramall-client / mediahub-client
//   고칠 때는 세 곳을 모두 같이 고칠 것.
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = defineProps({
  // 시세 목록. 칸의 생김새는 슬롯이 정하므로 여기서는 개수만 쓴다.
  items: { type: Array, default: () => [] },
  // 칸 사이 간격(px). 헤더마다 다르다.
  gap: { type: Number, default: 60 },
  // 흐르는 속도(px/초).
  speed: { type: Number, default: 40 },
})

// 폭을 못 쟀을 때 다시 재는 간격과 횟수(0.15초 x 20 = 3초).
const RETRY_MS = 150
const RETRY_MAX = 20

const clip = ref(null)
const track = ref(null)
// 이어 붙일 벌 수. 재서 정한다(아래 measure).
const copies = ref(2)

let anim = null
let observer = null
// 한 바퀴 거리를 한 번이라도 제대로 쟀는지. 못 쟀으면 기회가 생길 때마다 다시 잰다.
let settled = false
let retry = 0
let retryTimer = 0

const stop = () => {
  anim?.cancel()
  anim = null
}

// 한 바퀴 거리를 재고, 필요한 벌 수를 맞춘 뒤 띄운다.
const measure = async () => {
  stop()
  const n = props.items.length
  if (!n) return

  if (copies.value < 2) {
    copies.value = 2
    await nextTick()
  }
  if (!clip.value || !track.value) return

  const period = track.value.children[n]?.offsetLeft ?? 0
  if (period <= 0) {
    // 아직 자리를 못 잡았다(폭 0). 여기서 그냥 돌아서면 영영 멈춘 채로 남는다 —
    // 다시 재는 계기가 글꼴 로드와 목록 변경뿐이라 둘 다 이미 지나갔으면 기회가 없다.
    // 느린 기기나 백그라운드 탭에서 첫 측정이 레이아웃보다 빠를 수 있어 잠시 뒤 다시 잰다.
    if (retry < RETRY_MAX) {
      retry += 1
      window.clearTimeout(retryTimer)
      retryTimer = window.setTimeout(measure, RETRY_MS)
    }
    return
  }
  retry = 0
  settled = true

  const need = 1 + Math.ceil(clip.value.clientWidth / period)
  if (need !== copies.value) {
    copies.value = need
    await nextTick()
  }

  anim = track.value.animate(
    [{ transform: 'translateX(0px)' }, { transform: `translateX(${-period}px)` }],
    { duration: (period / props.speed) * 1000, iterations: Infinity, easing: 'linear' },
  )
}

// 폭이 넓어지면 벌 수만 늘린다. 한 바퀴 거리는 그대로라 다시 띄우지 않는다 —
// 다시 띄우면 흐르던 위치가 처음으로 튄다.
const onResize = () => {
  // 아직 한 번도 제대로 못 쟀다면 이 기회에 잰다. 폭이 이제 막 생긴 경우다.
  if (!settled) {
    measure()
    return
  }
  const n = props.items.length
  if (!n || !clip.value || !track.value || !anim) return
  const period = track.value.children[n]?.offsetLeft ?? 0
  if (period <= 0) return
  const need = 1 + Math.ceil(clip.value.clientWidth / period)
  if (need > copies.value) copies.value = need
}

onMounted(() => {
  measure()
  // 글꼴이 늦게 오면 글자 폭이 달라져 한 바퀴 거리도 달라진다. 오고 나서 한 번 더 잰다.
  document.fonts?.ready.then(measure)
  observer = new ResizeObserver(onResize)
  observer.observe(clip.value)
})

// 시세는 API 로 나중에 들어온다. 목록이 바뀌면 다시 잰다.
watch(() => props.items, () => nextTick(measure), { deep: true })

onBeforeUnmount(() => {
  stop()
  window.clearTimeout(retryTimer)
  observer?.disconnect()
  observer = null
})
</script>

<template>
  <div ref="clip" class="ticker-clip relative min-w-0 overflow-hidden">
    <div
      ref="track"
      class="flex h-full w-max items-center will-change-transform"
      :style="{ gap: `${gap}px` }"
    >
      <template v-for="copy in copies" :key="copy">
        <div
          v-for="item in items"
          :key="`${copy}-${item.label}`"
          class="flex shrink-0 items-center"
          :aria-hidden="copy > 1 || undefined"
        >
          <slot :item="item" />
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
/* 양끝을 흐리게 지운다.
   가리기만 하면 글자가 칼로 자른 듯 끊겨, 라벨 옆에 "4,000" 같은 숫자 조각이
   떠 있는 것처럼 보인다. 흐려 놓으면 밖에서 들어오고 밖으로 나가는 것으로 읽힌다. */
.ticker-clip {
  -webkit-mask-image: linear-gradient(
    to right,
    transparent 0,
    #000 24px,
    #000 calc(100% - 24px),
    transparent 100%
  );
  mask-image: linear-gradient(
    to right,
    transparent 0,
    #000 24px,
    #000 calc(100% - 24px),
    transparent 100%
  );
}
</style>
